import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
  textShowElement,
  textShowImgElement,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { channelColorMap } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { formatTimeXAxisText, getXAxisAxisLabel } from '@op/utils/chart';
import { DBO_EC, DBO_V, DBO_Y } from '@op/utils/format';
import { dealWithOthersName } from '@op/utils/languageValue';
import { TRequestBackType } from '@op/utils/requestUtil';
import React, { useMemo } from 'react';
import { KeyMetricsItemType } from '../../constants';

export default function CommonLineChart({
  eventsDict,
  lineData,
  metricItem,
  type = 'channel',
  filter,
  chartType,
}: {
  eventsDict: any;
  lineData: TRequestBackType | null;
  metricItem: KeyMetricsItemType;
  type: 'channel' | 'language';
  filter: any;
  chartType: 'bar' | 'line';
}) {
  const { formatMessage } = useLanguage();
  const createLineChartOptions = () => {
    const { isMin, series, legend, xAxis } = lineData || {};

    const sList = series?.map((item) => {
      const obj = {
        ...item,
        type: chartType,
        stack: chartType === 'bar' ? 'total' : '',
        data: item.data.map((value) => {
          if (item.name === 'other') {
            return { value, legend };
          }
          return value;
        }),
      };
      if (type === 'channel') {
        return { ...obj, itemStyle: { color: channelColorMap[item.name] } };
      }
      return obj;
    });

    const {
      format = (value: number) => DBO_V.reviews(value),
      text,
      isPercentage = false,
    } = metricItem || {};
    return {
      grid: {
        top: 20,
        contentLabel: true,
        left: 10,
        right: 5,
      },
      tooltip: {
        trigger: 'axis',
        appendToBody: false,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) =>
          DBO_EC.position(pos, params, dom, rect, size, -5),
        formatter: (params: any) => {
          const sumText = formatMessage({ id: text });
          return formatChartParamsDate({
            params,
            isSort: true,
            valueFormat: (value: number) => format(value),
            textShowFun: (marker: string, seriesName: string) => {
              if (type === 'channel') {
                const { name, img } = channelInfo(
                  seriesName.toLocaleLowerCase() as any,
                );
                return textShowImgElement(
                  marker,
                  dealWithOthersName(name, 'channel'),
                  img,
                );
              }

              return textShowElement(
                marker,
                dealWithOthersName(seriesName, 'language'),
              );
            },
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                const share = DBO_V.percentage(item.originValue, total, 2);
                return {
                  ...item,
                  value: `${item.value}${
                    !item.originValue || isPercentage ? '' : ` (${share})`
                  }`,
                };
              });
              if (!isPercentage) {
                newRecords.unshift({
                  text: formatMessage('Sum'),
                  value: format(total),
                  originValue: total,
                  originText: '',
                });
              }

              return newRecords;
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: listTimeTitleFunc(params[0]?.name, filter.date_type),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: sumText,
                },
              ];

              return [{ showHeader: true, records, columns }];
            },
          });
        },
      },
      legend: {
        data: legend,
        formatter: (value: any) => {
          if (type === 'channel') {
            return `{${dealwithChannelChartKey(
              value,
            )}|} {name|${dealWithOthersName(value, 'channel')}}`;
          }
          return dealWithOthersName(value, 'language');
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter.date_type),
      ),
      xAxis: {
        type: 'category',
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxis,
        boundaryGap: ['20%', 0],
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) =>
            DBO_Y.reviewsPercent(value, isPercentage, isMin),
        },
      },
      series: sList,
    };
  };

  const { options, isEmpty } = useMemo(() => {
    const { isEmpty = true } = lineData || {};
    if (isEmpty) {
      return { isEmpty, options: {} };
    }

    const options = createLineChartOptions();

    return { options, isEmpty: false };
  }, [formatMessage]);

  return (
    <EChartCommon
      optionsProps={{ type: chartType }}
      height={338}
      options={options}
      empty={isEmpty}
      loading={false}
      eventsDict={eventsDict}
    ></EChartCommon>
  );
}
